import { AddProjectForm } from "@/pages/ProjectDetailsPage/components/AddProjectModal";
import { UseFormSetError } from "react-hook-form";
import { validateAccountNumber } from "@/api/projectsApi";
import { validateAccountNumberFormat } from "./validateAccountNumber";
import { appendSolarFields } from "./appendSolarFields";

export const createProjectFormData = (
  data: AddProjectForm,
  role?: string,
  contractorEmail?: string
): FormData => {
  const formData = new FormData();

  formData.append("customerName", data.customerName || "");
  formData.append("accountHash", String(data.accountHash || ""));
  formData.append("accountNumber", String(data.accountHash || ""));
  formData.append("zipCode", data.zipCode || "");

  formData.append("city", data.city || "");
  const address = [data.streetAddress, data.city, data.state, data.zipCode]
    .filter(Boolean)
    .join(", ");
  formData.append("address", address);

  formData.append("accountEmail", data.accountEmail || "");
  formData.append("phoneNumber", data.phoneNumber || "");

  if (data.estimated_installation_date) {
    formData.append("estimated_installation_date", data.estimated_installation_date);
  }

  if (data.operationalizedDate) {
    formData.append("operationalizedDate", data.operationalizedDate);
  }

  formData.append("batteryOem", data.batteryManufacturer || "");
  formData.append("batteryModel", data.batteryProductName || "");
  
  // Include OEM and battery model IDs for proper foreign key relationships
  if (data.batteryOemId) {
    formData.append("oemId", String(data.batteryOemId));
  }
  if (data.batteryModelId) {
    formData.append("batteryModelId", String(data.batteryModelId));
  }
  
  formData.append("usableCapacity", String(data.usableCapacity || ""));
  if (data.usableCapacityIncentive) {
    formData.append(
      "usableCapacityIncentive",
      String(data.usableCapacityIncentive)
    );
  }
  formData.append("numberOfBatteries", String(data.batteryQuantity || ""));

  const upfrontIncentiveInCents =
    Number(data.upfrontIncentivePayment || 0) * 100;
  formData.append("upfrontIncentivePayment", String(upfrontIncentiveInCents));

  const solarSystemTypes = Array.isArray(data.solarSystemType)
    ? data.solarSystemType
    : [];
  const hasExistingSolar = solarSystemTypes.includes("existing");
  const hasNewSolar = solarSystemTypes.includes("new");

  formData.append("hasExistingSolar", String(hasExistingSolar));
  formData.append("hasNewSolar", String(hasNewSolar));

  appendSolarFields(
    formData,
    {
      solarKwdc: data.solarKwdc,
      solarKwac: data.solarKwac,
      solarAnnualProduction: data.solarAnnualProduction,
      newSolarKwdc: data.newSolarKwdc,
      newSolarKwac: data.newSolarKwac,
      newSolarAnnualProduction: data.newSolarAnnualProduction,
    },
    { hasExistingSolar, hasNewSolar }
  );

  formData.append("existingInverter", data.inverterName || "");
  formData.append("inverterManufacturer", data.inverterManufacturer || "");

  if (data.totalProjectCost !== undefined) {
    formData.append("total_cost", String(data.totalProjectCost || ""));
  }
  if (data.externalIncentives !== undefined) {
    formData.append("extra_incentives", String(data.externalIncentives || ""));
  }
  if (data.externalIncentivePrograms !== undefined) {
    formData.append(
      "externalIncentivePrograms",
      String(data.externalIncentivePrograms || "")
    );
  }

  if (data.thirdPartyOwned !== undefined) {
    formData.append("thirdPartyOwned", String(data.thirdPartyOwned));
  }

  if (role === "administrator" && (data.contractor || data.contractorId)) {
    if (data.contractor) {
      formData.append("contractor", data.contractor);
    }
    if (data.contractorId) {
      formData.append("contractorId", String(data.contractorId));
    }
  }

  // Automatically set termsAccepted to true if OEM requires terms
  // OEMs requiring terms: Tesla, Enphase, Fortress, Panasonic
  const oemsRequiringTerms = ['Tesla', 'Enphase', 'Fortress', 'Panasonic'];
  const batteryManufacturer = data.batteryManufacturer || '';
  const requiresTerms = oemsRequiringTerms.some(oem => 
    batteryManufacturer.toLowerCase().includes(oem.toLowerCase())
  );
  
  if (requiresTerms) {
    formData.append("termsAccepted", "true");
  }

  if (data.termsAndConditionsFile) {
    if (data.termsAndConditionsFile instanceof File) {
      formData.append("termsAndConditionsFile", data.termsAndConditionsFile);
    }
  }

  if (data.payeeName) {
    formData.append("payeeName", data.payeeName);
  }

  if (data.payeeType) {
    formData.append("payeeType", data.payeeType);
  }

  if (data.payeeAddress) {
    formData.append("mailingAddress", data.payeeAddress);
  }
  if (data.payeeCity) {
    formData.append("mailingCity", data.payeeCity);
  }
  if (data.payeeState) {
    formData.append("mailingState", data.payeeState);
  }
  if (data.payeeZip) {
    formData.append("mailingZipCode", data.payeeZip);
  }

  let payeeEmail = data.accountEmail;
  if (data.payeeType === "contractor") {
    payeeEmail = contractorEmail || data.accountEmail;
  } else if (data.payeeType === "customer" || data.payeeType === "admin") {
    payeeEmail = data.accountEmail;
  }

  if (payeeEmail) {
    formData.append("payeeMailAdr", payeeEmail);
  }

  return formData;
};

export const validateStepTransition = async (
  step: 1 | 2 | 3 | 4 | 5,
  setError: UseFormSetError<AddProjectForm>,
  accountHash?: string,
  zipCode?: string
): Promise<boolean> => {
  if (step === 1) {
    return true;
  }

  if (step === 2) {
    // Validate account number if provided
    if (accountHash) {
      // First check format
      const formatValidation = validateAccountNumberFormat(accountHash);
      if (!formatValidation.valid) {
        setError("accountHash", { message: formatValidation.message });
        return false;
      }

      // Then validate with API
      try {
        await validateAccountNumber(accountHash, zipCode);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Account number not found. Please check and try again.";

        if (errorMessage.toLowerCase().includes("zipcode")) {
          setError("zipCode", { message: errorMessage });
        } else {
          setError("accountHash", { message: errorMessage });
        }
        return false;
      }
    }

    return true;
  }

  return true;
};
